var _react_refresh_temp_1;
var _react_refresh_temp_2;
_react_refresh_temp_2 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffectEvent, useLayoutEffect, useRef, } from 'react';
import { ESCAPE_KEY } from '#constants';
import { C } from '#integrations';
import { Box } from '../Box';
import { IconButton } from '../buttons';
import { XCloseIcon } from '../icons';
import { useToastService } from '../Toast';
import { Typography } from '../Typography';
import { ModalTestIdFactory } from './interactions';
import { MODAL_HEIGHT_MODES, MODAL_WIDTHS } from './Modal.constants';
import styles from './Modal.module.css';
const WIDTH_CLASS_MAP = {
    l: styles.ModalWidthLarge,
    m: styles.ModalWidthMedium,
    s: styles.ModalWidthSmall,
};
const VERTICAL_POSITIONING_CLASS_MAP = {
    center: undefined,
    top: styles.ContainerTop,
};
export const Modal = ({ 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, children, className, closeModal, 'data-testid': testId, heightMode = MODAL_HEIGHT_MODES.Contents, isFullScreen, modalLabel, modalWidth = MODAL_WIDTHS.Medium, padding = 'default', shouldCloseOnOverlayClick = true, shouldHaveCloseButton = true, verticalPositioning = 'center', }) => {
    _react_refresh_temp_2();
    const dialogRef = useRef(null);
    const isMouseDownOnOverlayRef = useRef(false);
    const defaultPadding = modalLabel ? '2xl xl xl' : 'xl';
    const contentPadding = padding === 'compact' ? 'm' : defaultPadding;
    // We need to always show toast on top of Modals in the top-layer
    // Therefore, we need to trigger .showPopover()
    // Each time a Modal is shown
    // https://lucidlink.atlassian.net/browse/LUCID-17542
    const { bringToastsToTop } = useToastService();
    const onOverlayMouseDown = useCallback((event) => {
        isMouseDownOnOverlayRef.current = event.target === dialogRef.current;
    }, []);
    const onOverlayClick = useCallback((event) => {
        event.stopPropagation();
        // A drag that starts on the content but ends on the backdrop still fires
        // a click on the dialog - that must not close it. `detail` is 0 for
        // keyboard and programmatic clicks, which have no mouse down of their own.
        const isDragFromContent = event.detail > 0 && !isMouseDownOnOverlayRef.current;
        if (dialogRef &&
            event.target === dialogRef.current &&
            !isDragFromContent &&
            shouldCloseOnOverlayClick) {
            closeModal();
        }
    }, [shouldCloseOnOverlayClick, closeModal]);
    const handleKeys = useEffectEvent((e) => {
        if (e.defaultPrevented) {
            return;
        }
        if (e.key === ESCAPE_KEY) {
            e.preventDefault();
            closeModal();
        }
    });
    const onShow = useEffectEvent(() => {
        bringToastsToTop();
    });
    const hasContent = Boolean(children);
    // useLayoutEffect so close() runs before the dialog leaves the DOM; a re-run
    // would reopen the dialog and reset focus, hence no callback dependencies.
    useLayoutEffect(() => {
        const dialog = dialogRef.current;
        if (!hasContent || !dialog) {
            return;
        }
        const onWindowKeyDown = (event) => handleKeys(event);
        dialog.showModal();
        onShow();
        window.addEventListener('keydown', onWindowKeyDown);
        return () => {
            dialog.close();
            window.removeEventListener('keydown', onWindowKeyDown);
        };
    }, [hasContent]);
    return children ? (_jsx("dialog", { "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, className: C(styles.Container, VERTICAL_POSITIONING_CLASS_MAP[verticalPositioning], isFullScreen && styles.FullScreen), onClick: onOverlayClick, onMouseDown: onOverlayMouseDown, ref: dialogRef, ...(testId && {
            'data-testid': ModalTestIdFactory.getModalDialogTestId(testId),
        }), children: _jsxs(Box, { className: C(styles.Modal, isFullScreen
                ? styles.FullScreen
                : WIDTH_CLASS_MAP[modalWidth], heightMode === MODAL_HEIGHT_MODES.Fixed &&
                styles.ModalHeightFixed, className), gap: "l", kind: "level2", orientation: "vertical", padding: contentPadding, radius: "l", children: [modalLabel && (_jsx("span", { className: styles.ModalLabel, children: _jsx(Typography, { "data-testid": "modal-label", fontWeight: "medium", kind: "generic-tiny", children: modalLabel }) })), shouldHaveCloseButton && (_jsx(IconButton
                // TODO(LUCID-35065): hardcoded English label; take it as a prop from the app.
                , { "aria-label": "close modal", className: styles.CloseButton, "data-testid": ModalTestIdFactory.getModalCloseButtonBase(), Icon: XCloseIcon, kind: "ghost", onClick: closeModal, size: "medium" })), children] }) })) : null;
};
_react_refresh_temp_1 = Modal;
$RefreshReg$(_react_refresh_temp_1, "Modal");
_react_refresh_temp_2(Modal, "6aGRGEQsIgRJkvgL651tzok9vaA=", false, () => [useToastService, useEffectEvent, useEffectEvent]);
